import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { formatDate, rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GitMerge, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const LABEL = {
  attendance: "Absensi", salary_rates: "Master Gaji", cash_advances: "Kasbon", kasbon_requests: "Pengajuan Kasbon",
  payroll_details: "Slip Gaji", employee_allowances: "Tunjangan", deductions: "Potongan", employee_projects: "Riwayat Proyek",
  attendance_rejections: "Absen Ditolak", attendance_moves: "Riwayat Pindah", users: "Akun Login",
};
const norm = (s) => (s || "").toLowerCase().split(/\s+/).filter(Boolean).join(" ");

export default function MergeEmployeeDialog({ keep, rows, onClose, onDone }) {
  const [dupId, setDupId] = useState("");
  const [pv, setPv] = useState(null);
  const [pilihan, setPilihan] = useState({});
  const [rateFrom, setRateFrom] = useState("keep");
  const [busy, setBusy] = useState(false);
  const cands = rows.filter((r) => r.id !== keep.id && !r.merged_into)
    .sort((a, b) => (norm(b.nama) === norm(keep.nama)) - (norm(a.nama) === norm(keep.nama)) || (a.nama || "").localeCompare(b.nama || ""));

  useEffect(() => {
    setPv(null); setPilihan({});
    if (!dupId) return;
    api.get(`/employees-merge/preview?keep_id=${keep.id}&dup_id=${dupId}`)
      .then((r) => { setPv(r.data); setRateFrom(r.data.rates.keep || !r.data.rates.dup ? "keep" : "dup"); })
      .catch((e) => toast.error(apiErr(e)));
  }, [dupId, keep.id]);

  const ready = pv && pv.conflicts.every((c) => pilihan[c.tanggal]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/employees-merge", { keep_id: keep.id, dup_id: dupId, pilihan, rate_from: rateFrom });
      toast.success(`Data digabung ke ${keep.nama}${r.data.absensi_dihapus ? ` · ${r.data.absensi_dihapus} absensi bentrok dihapus` : ""}`);
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GitMerge className="h-5 w-5 text-orange-500" /> Gabung Data Karyawan</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
          Data utama (dipertahankan): <b>{keep.nama}</b> <span className="text-xs">{keep.kode_pegawai}</span>
        </div>
        <div>
          <div className="mb-1 text-xs font-semibold text-slate-600">Data kedua (akan dipindah & dinonaktifkan)</div>
          <Select value={dupId} onValueChange={setDupId}>
            <SelectTrigger data-testid="merge-dup-select"><SelectValue placeholder="Pilih data karyawan kembar" /></SelectTrigger>
            <SelectContent>{cands.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.nama} · {c.kode_pegawai}{norm(c.nama) === norm(keep.nama) ? " (nama sama)" : ""}</SelectItem>
            ))}</SelectContent>
          </Select>
        </div>
        {pv && <MergeBody pv={pv} pilihan={pilihan} setPilihan={setPilihan} rateFrom={rateFrom} setRateFrom={setRateFrom} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="merge-cancel">Batal</Button>
          <Button className="bg-orange-600 hover:bg-orange-700" disabled={!ready || busy} onClick={submit} data-testid="merge-submit">
            <GitMerge className="h-4 w-4 mr-1" /> {busy ? "Menggabungkan..." : "Gabungkan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MergeBody = ({ pv, pilihan, setPilihan, rateFrom, setRateFrom }) => (
  <div className="space-y-4">
    <div data-testid="merge-counts">
      <div className="mb-1 text-xs font-semibold text-slate-600">Yang dipindah dari {pv.dup.nama}</div>
      <div className="flex flex-wrap gap-2">
        {Object.keys(pv.counts).length === 0 && <span className="text-sm text-slate-400">Tidak ada riwayat</span>}
        {Object.entries(pv.counts).map(([k, n]) => (
          <span key={k} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{LABEL[k] || k}: {n}</span>
        ))}
      </div>
    </div>
    {pv.conflicts.length > 0 && <Conflicts pv={pv} pilihan={pilihan} setPilihan={setPilihan} />}
    <RatePick pv={pv} rateFrom={rateFrom} setRateFrom={setRateFrom} />
  </div>
);

const Conflicts = ({ pv, pilihan, setPilihan }) => {
  const all = (side) => setPilihan(Object.fromEntries(pv.conflicts.map((c) => [c.tanggal, side])));
  const cell = (a) => <span>{a.project_nama === "-" ? "Tanpa proyek" : a.project_nama} · {a.status}{Number(a.jam_lembur) ? ` · lembur ${a.jam_lembur}j` : ""}<span className="block text-[11px] text-slate-400">input: {a.input_by || "-"}</span></span>;
  return (
    <div className="rounded-lg border border-rose-200" data-testid="merge-conflicts">
      <div className="flex flex-wrap items-center gap-2 bg-rose-50 px-3 py-2 text-sm text-rose-800">
        <AlertTriangle className="h-4 w-4" /><b className="flex-1">{pv.conflicts.length} tanggal bentrok — pilih absensi yang dipakai</b>
        <Button size="sm" variant="outline" onClick={() => all("keep")} data-testid="merge-all-keep">Semua: utama</Button>
        <Button size="sm" variant="outline" onClick={() => all("dup")} data-testid="merge-all-dup">Semua: kedua</Button>
      </div>
      <div className="divide-y text-sm">{pv.conflicts.map((c) => (
        <div key={c.tanggal} className="grid grid-cols-1 sm:grid-cols-[90px_1fr_1fr] gap-2 px-3 py-2" data-testid={`merge-conflict-${c.tanggal}`}>
          <span className="font-mono font-semibold">{formatDate(c.tanggal)}</span>
          {["keep", "dup"].map((s) => (
            <label key={s} className={`flex cursor-pointer gap-2 rounded-md border p-2 ${pilihan[c.tanggal] === s ? "border-orange-500 bg-orange-50" : "border-slate-200"}`}>
              <input type="radio" name={`c-${c.tanggal}`} checked={pilihan[c.tanggal] === s} data-testid={`merge-pick-${c.tanggal}-${s}`}
                onChange={() => setPilihan((p) => ({ ...p, [c.tanggal]: s }))} />
              <span className="text-xs"><b>{s === "keep" ? "Utama" : "Kedua"}:</b> {cell(c[s])}</span>
            </label>
          ))}
        </div>
      ))}</div>
    </div>
  );
};

const RatePick = ({ pv, rateFrom, setRateFrom }) => (
  <div data-testid="merge-rate">
    <div className="mb-1 text-xs font-semibold text-slate-600">Master Gaji yang dipakai</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {["keep", "dup"].map((s) => {
        const r = pv.rates[s];
        return (
          <label key={s} className={`flex gap-2 rounded-md border p-2 text-xs ${r ? "cursor-pointer" : "opacity-50"} ${rateFrom === s ? "border-orange-500 bg-orange-50" : "border-slate-200"}`}>
            <input type="radio" name="rate" disabled={!r} checked={rateFrom === s} onChange={() => setRateFrom(s)} data-testid={`merge-rate-${s}`} />
            <span><b>{s === "keep" ? pv.keep.nama : pv.dup.nama}</b> <span className="text-slate-500">{s === "keep" ? pv.keep.kode_pegawai : pv.dup.kode_pegawai} · {s === "keep" ? "utama" : "kedua"}</span>
              {r ? <span className="block">Gaji/hari {rupiah(r.gaji_per_hari)} · Makan {rupiah(r.uang_makan_per_hari)} · Lembur {rupiah(r.tarif_lembur_per_jam)}/jam</span>
                : <span className="block text-slate-400">Belum ada Master Gaji</span>}
            </span>
          </label>
        );
      })}
    </div>
  </div>
);
